/*
 * Live activity: pure model, no React and nothing that touches `window`. The event shape, parsing a stream message,
 * merging it into the newest-first list, the reconnect backoff and the copy of each row.
 */
import { EventEnvelope, type StreamTopic } from '@hangar/shared';

export type StreamStatus = 'connecting' | 'connected' | 'reconnecting';

/** `/stream?topics=` values the console subscribes to: @hangar/shared's `StreamTopic` plus `shadow` (P3, in the contract). */
export type LiveTopic = StreamTopic | 'shadow';

export interface LiveEvent {
  id: string;
  type: string;
  occurredAt: string;
  projectId: string | null;
  subject: { type: string; id: string };
  data: Record<string, unknown>;
}

export const MAX_BACKOFF_MS = 30_000;

/** The `data:` payload of one SSE message as a `LiveEvent`, or `null` when it is not valid JSON or not an envelope. */
export function parseEnvelope(data: string): LiveEvent | null {
  try {
    const parsed = EventEnvelope.safeParse(JSON.parse(data));
    if (!parsed.success) return null;
    const e = parsed.data;
    return { id: e.id, type: e.type, occurredAt: e.occurredAt, projectId: e.projectId, subject: e.subject, data: e.data };
  } catch {
    return null;
  }
}

/** Newest first, capped; an event already in the list (same id, e.g. replayed after a reconnect) changes nothing. */
export function mergeEvent(prev: LiveEvent[], ev: LiveEvent, cap: number): LiveEvent[] {
  return prev.some((p) => p.id === ev.id) ? prev : [ev, ...prev].slice(0, cap);
}

/** The server's advertised `retry:` never drops below half a second. */
export const retryFloor = (ms: number): number => Math.max(500, ms);

/** Wait before the next attempt: the retry doubled per consecutive failure (up to 16x), capped, plus up to 400 ms of jitter. */
export function backoffMs(retryMs: number, failures: number, random: number): number {
  return Math.min(retryMs * 2 ** Math.min(failures - 1, 4), MAX_BACKOFF_MS) + random * 400;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

export interface EventCopy {
  title: string;
  slug?: string;
  status?: string;
  icon: string;
}

const BUILD_VERB = new Map([
  ['build.created', 'requested'],
  ['build.succeeded', 'succeeded'],
  ['build.failed', 'failed'],
  ['build.canceled', 'canceled'],
]);

/** A provider webhook: operators read which provider ("EAS webhook received"); clients never do (SPEC decision 23). */
function describeInbound(d: Record<string, unknown>, seesProviders: boolean): EventCopy {
  const source = str(d.source)?.toUpperCase() ?? 'Provider';
  return { title: seesProviders ? `${source} webhook received` : 'Build service update received', slug: str(d.appSlug), status: str(d.status), icon: 'hub' };
}

function describeBuild(type: string, d: Record<string, unknown>): EventCopy {
  const build = isRecord(d.build) ? d.build : {};
  const ref = str(build.ref);
  const verb = BUILD_VERB.get(type) ?? 'updated';
  return { title: ref ? `Build ${ref} ${verb}` : type, slug: str(build.appSlug) ?? str(d.appSlug), status: str(d.toStatus) ?? str(build.status) ?? str(d.status), icon: 'construction' };
}

/** Row copy: "EAS webhook received · storeai-mobile · finished · 12 s ago" (operators). Other event types render generically. */
export function describeEvent(ev: LiveEvent, seesProviders = false): EventCopy {
  if (ev.type === 'inbound.received') return describeInbound(ev.data, seesProviders);
  if (ev.type.startsWith('build.')) return describeBuild(ev.type, ev.data);
  return { title: ev.type, slug: str(ev.data.appSlug), status: str(ev.data.status), icon: 'bolt' };
}
